import React, { useEffect, useMemo, useRef, useState } from 'react';
import Fuse from 'fuse.js';
import Button from '../../Button';
import SearchAddButton from '../../SearchAddButton';
import Dropdown from '../../Dropdown';
import { saveElementaryHomeroomGrades } from '../../../js/SaveFunctions';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus, faLock, faPenToSquare, faSearch, faTimes, faTrashCan } from '@fortawesome/free-solid-svg-icons';

import '../../../css/elementary/manage_homerooms.css';

const DEFAULT_GRADE_VALUES = ['PK', 'K', '1', '2', '3', '4', '5', '6', '7', '8'];
const CUSTOM_GRADE_VALUE = '__custom_grade__';

const DEFAULT_GRADE_OPTIONS = [
	{value:'PK', display:'PreK'},
	{value:'K', display:'Kindergarten'},
	{value:'1', display:'Grade 1'},
	{value:'2', display:'Grade 2'},
	{value:'3', display:'Grade 3'},
	{value:'4', display:'Grade 4'},
	{value:'5', display:'Grade 5'},
	{value:'6', display:'Grade 6'},
	{value:'7', display:'Grade 7'},
	{value:'8', display:'Grade 8'},
	{value:CUSTOM_GRADE_VALUE, display:'Custom Grade Name'}
];

const normalizeGradeLabel = (gradeValue) => {
	if(gradeValue === null || typeof gradeValue === 'undefined') return '';
	const gradeString = `${gradeValue}`.trim();
	if(gradeString === '0') return 'K';
	if(gradeString.toLowerCase() === 'k') return 'K';
	return gradeString;
};

const displayTeacherName = (teacher) => {
	if(!teacher) return '';
	const firstName = (teacher.first_name) ? `${teacher.first_name}`.trim() : '';
	const lastName = (teacher.name) ? `${teacher.name}`.trim() : '';
	return `${lastName}, ${firstName}`.replace(/,\s*$/, '');
};

const sortTeachersByName = (teacherA, teacherB) => {
	const nameA = displayTeacherName(teacherA).toLowerCase();
	const nameB = displayTeacherName(teacherB).toLowerCase();
	if(nameA < nameB) return -1;
	if(nameA > nameB) return 1;
	return 0;
};

const normalizeLabelForCompare = (label) => `${label}`.trim().toLowerCase();
const findDefaultGradeValueFromLabel = (label) => {
	const normalizedLabel = normalizeLabelForCompare(label);
	if(!normalizedLabel) return null;

	const matchedOption = DEFAULT_GRADE_OPTIONS.find((option) => {
		if(option.value === CUSTOM_GRADE_VALUE) return false;
		return normalizeLabelForCompare(option.value) === normalizedLabel || normalizeLabelForCompare(option.display) === normalizedLabel;
	});

	return (matchedOption) ? matchedOption.value : null;
};

const sanitizeGroups = (groups, teachers) => {
	if(!Array.isArray(groups)) return [];

	const normalizedTeachers = Array.isArray(teachers) ? teachers : [];
	const teacherIds = new Set(normalizedTeachers.map(teacher => `${teacher.teacher_id}`));
	const shouldValidateTeacherIds = teacherIds.size > 0;

	return groups.reduce((results, group, index) => {
		const rawLabel = (group && group.label) ? `${group.label}`.trim() : '';
		const label = (rawLabel.length > 0) ? rawLabel : `${index + 1}`;

		const teacherList = (group && Array.isArray(group.teacher_ids)) ? group.teacher_ids : [];
		const uniqueTeacherIds = [];
		teacherList.forEach((teacherId) => {
			const normalizedTeacherId = `${teacherId || ''}`.trim();
			if(!normalizedTeacherId || uniqueTeacherIds.includes(normalizedTeacherId)) return;
			if(shouldValidateTeacherIds && !teacherIds.has(normalizedTeacherId)) return;
			uniqueTeacherIds.push(normalizedTeacherId);
		});

		results.push({
			id: (group && group.id) ? group.id : `${Date.now()}-${index}`,
			label,
			teacher_ids: uniqueTeacherIds
		});

		return results;
	}, []);
};

const createComparableGroups = (groups) => {
	if(!Array.isArray(groups)) return [];
	return groups.map((group) => ({
		label: `${group.label || ''}`.trim(),
		teacher_ids: (Array.isArray(group.teacher_ids) ? [...group.teacher_ids] : []).map(teacher_id => `${teacher_id}`).sort()
	})).sort((a, b) => a.label.localeCompare(b.label));
};

const areGradeGroupsEqual = (groupsA, groupsB) => {
	const comparableA = createComparableGroups(groupsA);
	const comparableB = createComparableGroups(groupsB);
	return JSON.stringify(comparableA) === JSON.stringify(comparableB);
};

const inferGroupsFromData = (teachers, students) => {
	const groupedTeacherIds = {};

	(students || []).forEach((student) => {
		const homeroomTeacherId = student.homeroom_teacher_id;
		if(!homeroomTeacherId) return;

		const label = normalizeGradeLabel(student.grade);
		if(!label) return;

		if(!(label in groupedTeacherIds)) groupedTeacherIds[label] = [];
		if(!groupedTeacherIds[label].includes(homeroomTeacherId)) groupedTeacherIds[label].push(homeroomTeacherId);
	});

	const allAssignedTeacherIds = Object.values(groupedTeacherIds).flat();
	const unassignedHomeroomTeacherIds = (teachers || []).reduce((results, teacher) => {
		if(teacher.is_homeroom_teacher !== '1') return results;
		if(allAssignedTeacherIds.includes(teacher.teacher_id)) return results;
		results.push(teacher.teacher_id);
		return results;
	}, []);

	if(unassignedHomeroomTeacherIds.length > 0) groupedTeacherIds.Unassigned = unassignedHomeroomTeacherIds;

	const labels = Object.keys(groupedTeacherIds).sort((labelA, labelB) => {
		if(labelA === 'PK') return -1;
		if(labelB === 'PK') return 1;
		if(labelA === 'K') return -1;
		if(labelB === 'K') return 1;
		if(labelA === 'Unassigned') return 1;
		if(labelB === 'Unassigned') return -1;

		const labelANumber = parseInt(labelA, 10);
		const labelBNumber = parseInt(labelB, 10);
		const labelAIsNumber = !Number.isNaN(labelANumber);
		const labelBIsNumber = !Number.isNaN(labelBNumber);

		if(labelAIsNumber && labelBIsNumber) return labelANumber - labelBNumber;
		return labelA.localeCompare(labelB);
	});

	return labels.map((label, index) => ({
		id: `${Date.now()}-${index}`,
		label,
		teacher_ids: groupedTeacherIds[label]
	}));
};

export default function ManageHomerooms({ subscreenRestricted, subscreenLocked, schoolInfo, teachers = [], students = [], initialHomeroomGroups = null, initialHomeroomGroupsLoading = false, onChangeHomeroomGroups = ()=>{}, handleMoveToNextScreen = ()=>{} }) {
	const [gradeGroups, setGradeGroups] = useState([]);
	const [initialLoadComplete, setInitialLoadComplete] = useState(false);

	const [gradeSearchOpen, setGradeSearchOpen] = useState({});
	const [gradeSearchResults, setGradeSearchResults] = useState({});

	const [addGradeOpen, setAddGradeOpen] = useState(false);
	const [addGradeValue, setAddGradeValue] = useState('K');
	const [addCustomGradeName, setAddCustomGradeName] = useState('');

	const [editingGradeId, setEditingGradeId] = useState(null);
	const [editGradeValue, setEditGradeValue] = useState('K');
	const [editCustomGradeName, setEditCustomGradeName] = useState('');

	const savingInProgressRef = useRef(false);
	const pendingSaveRef = useRef(false);
	const latestGradeGroupsRef = useRef([]);
	const saveErrorLoggedRef = useRef(false);
	const lastSavedComparableRef = useRef(null);

	const storageKey = useMemo(() => {
		const schoolId = (schoolInfo && schoolInfo.school_id) ? schoolInfo.school_id : 'unknown-school';
		const scheduleVersionId = (schoolInfo && schoolInfo.current_schedule_version_id) ? schoolInfo.current_schedule_version_id : 'unknown-schedule';
		return `elem_manage_homerooms_${schoolId}_${scheduleVersionId}`;
	}, [schoolInfo]);

	const sortedTeachers = useMemo(() => {
		const teacherCopy = [...teachers];
		teacherCopy.sort(sortTeachersByName);
		return teacherCopy;
	}, [teachers]);

	const hasTeacherAssignments = gradeGroups.some(group => group.teacher_ids.length > 0);
	const gradeDropdownOptions = useMemo(() => DEFAULT_GRADE_OPTIONS.map(option => ({...option})), []);

	const isLabelInUse = (newLabel, excludedGradeId = null) => {
		const normalized = normalizeLabelForCompare(newLabel);
		if(!normalized) return false;
		return gradeGroups.some(group => group.id !== excludedGradeId && normalizeLabelForCompare(group.label) === normalized);
	};

	const getUnusedDefaultGrade = () => {
		const usedLabels = new Set(gradeGroups.map(group => normalizeLabelForCompare(group.label)));
		const unused = DEFAULT_GRADE_VALUES.find(label => !usedLabels.has(normalizeLabelForCompare(label)));
		return unused || 'K';
	};

	const getSelectedGradeLabel = (selectedValue, customName) => {
		if(selectedValue === CUSTOM_GRADE_VALUE) return customName.trim();

		const grade_option_display = DEFAULT_GRADE_OPTIONS.find(grade_option => grade_option.value === selectedValue).display;

		return grade_option_display;
	};

	const getAvailableTeachersForGrade = (gradeId) => {
		const assignedElsewhere = gradeGroups.reduce((results, group) => {
			if(group.id === gradeId) return results;
			group.teacher_ids.forEach((teacherId) => results.add(teacherId));
			return results;
		}, new Set());

		return sortedTeachers.filter((teacher) => !assignedElsewhere.has(teacher.teacher_id));
	};

	const openAddGrade = () => {
		setAddGradeOpen(true);
		setAddGradeValue(getUnusedDefaultGrade());
		setAddCustomGradeName('');
	};

	const saveNewGrade = () => {
		const label = getSelectedGradeLabel(addGradeValue, addCustomGradeName);
		if(!label) return;
		if(isLabelInUse(label)) return;

		const newGrade = {
			id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
			label,
			teacher_ids: []
		};
		setGradeGroups([...gradeGroups, newGrade]);
		setAddGradeOpen(false);
		setAddCustomGradeName('');
	};

	const startEditGrade = (gradeId) => {
		const gradeInfo = gradeGroups.find(group => group.id === gradeId);
		if(!gradeInfo) return;

		const existingLabel = gradeInfo.label;
		const defaultGradeValue = findDefaultGradeValueFromLabel(existingLabel);
		const isDefault = !!defaultGradeValue;

		setEditingGradeId(gradeId);
		setEditGradeValue(isDefault ? defaultGradeValue : CUSTOM_GRADE_VALUE);
		setEditCustomGradeName(isDefault ? '' : existingLabel);
	};

	const saveEditedGrade = () => {
		if(!editingGradeId) return;
		const newLabel = getSelectedGradeLabel(editGradeValue, editCustomGradeName);
		if(!newLabel) return;
		if(isLabelInUse(newLabel, editingGradeId)) return;

		const updated = gradeGroups.map((group) => {
			if(group.id !== editingGradeId) return group;
			return {...group, label:newLabel};
		});

		setGradeGroups(updated);
		setEditingGradeId(null);
	};

	const removeGrade = (gradeId) => {
		const updatedGroups = gradeGroups.filter(group => group.id !== gradeId);
		const openSearchCopy = {...gradeSearchOpen};
		const searchResultsCopy = {...gradeSearchResults};
		delete openSearchCopy[gradeId];
		delete searchResultsCopy[gradeId];

		setGradeSearchOpen(openSearchCopy);
		setGradeSearchResults(searchResultsCopy);
		setGradeGroups(updatedGroups);
		if(editingGradeId === gradeId) setEditingGradeId(null);
	};

	const toggleTeacherSearch = (gradeId) => {
		const newValue = !gradeSearchOpen[gradeId];
		const openCopy = {...gradeSearchOpen, [gradeId]: newValue};
		const resultsCopy = {...gradeSearchResults};
		if(newValue) resultsCopy[gradeId] = getAvailableTeachersForGrade(gradeId);

		setGradeSearchOpen(openCopy);
		setGradeSearchResults(resultsCopy);
	};

	const fuzzySearchTeachers = (gradeId, searchValue) => {
		const availableTeachers = getAvailableTeachersForGrade(gradeId);
		const teacherResults = (searchValue.length > 0) ? (() => {
			const fuse = new Fuse(availableTeachers, {keys: ['first_name', 'name'], threshold: .1});
			return fuse.search(searchValue).map(result => result.item).slice(0, 8);
		})() : availableTeachers;

		setGradeSearchResults({...gradeSearchResults, [gradeId]: teacherResults});
	};

	const addTeacherToGrade = (gradeId, teacherId) => {
		const gradeIndex = gradeGroups.findIndex(group => group.id === gradeId);
		if(gradeIndex === -1) return;

		if(gradeGroups[gradeIndex].teacher_ids.includes(teacherId)) return;

		const groupsCopy = [...gradeGroups];
		groupsCopy[gradeIndex].teacher_ids.push(teacherId);
		setGradeGroups(groupsCopy);

	};

	const removeTeacherFromGrade = (gradeId, teacherId) => {
		const groupsCopy = gradeGroups.map(group => {
			if(group.id !== gradeId) return group;
			return {...group, teacher_ids: group.teacher_ids.filter(currentTeacherId => currentTeacherId !== teacherId)};
		});

		setGradeGroups(groupsCopy);
	};

	useEffect(() => {
		if(initialHomeroomGroupsLoading)
		{
			setInitialLoadComplete(false);
			return;
		}

		if(Array.isArray(initialHomeroomGroups))
		{
			const sanitizedGroups = sanitizeGroups(initialHomeroomGroups, teachers);
			setGradeGroups((previousGroups) => areGradeGroupsEqual(previousGroups, sanitizedGroups) ? previousGroups : sanitizedGroups);
			lastSavedComparableRef.current = JSON.stringify(createComparableGroups(sanitizedGroups));
			setInitialLoadComplete(true);
			return;
		}

		const storedValue = sessionStorage.getItem(storageKey);
		if(storedValue) {
				try {
						const parsedValue = JSON.parse(storedValue);
						const sanitized = sanitizeGroups(parsedValue, teachers);
						setGradeGroups((previousGroups) => areGradeGroupsEqual(previousGroups, sanitized) ? previousGroups : sanitized);
						lastSavedComparableRef.current = JSON.stringify(createComparableGroups(sanitized));
						setInitialLoadComplete(true);
						return;
					} catch (error) {
					console.log('Unable to parse saved homeroom groups', error);
				}
			}

		const inferredGroups = inferGroupsFromData(teachers, students);
		setGradeGroups((previousGroups) => areGradeGroupsEqual(previousGroups, inferredGroups) ? previousGroups : inferredGroups);
		setInitialLoadComplete(true);
	}, [initialHomeroomGroups, initialHomeroomGroupsLoading, storageKey, teachers, students]);

	useEffect(() => {
		sessionStorage.setItem(storageKey, JSON.stringify(gradeGroups));
		latestGradeGroupsRef.current = gradeGroups;
	}, [gradeGroups, storageKey]);

	useEffect(() => {
		if(!initialLoadComplete) return;
		if(initialHomeroomGroupsLoading) return;
		onChangeHomeroomGroups(gradeGroups);
	}, [gradeGroups, initialLoadComplete, initialHomeroomGroupsLoading, onChangeHomeroomGroups]);

	useEffect(() => {
		if(!initialLoadComplete) return;
		if(initialHomeroomGroupsLoading) return;
		const school_id = (schoolInfo && schoolInfo.school_id) ? schoolInfo.school_id : null;
		const schedule_version_id = (schoolInfo && schoolInfo.current_schedule_version_id) ? schoolInfo.current_schedule_version_id : null;
		if(!school_id || !schedule_version_id) return;
		const comparableGroups = JSON.stringify(createComparableGroups(gradeGroups));
		if(lastSavedComparableRef.current === comparableGroups) return;
		if(savingInProgressRef.current)
		{
			pendingSaveRef.current = true;
			return;
		}

		const saveGroups = (groupsToSave) => {
			savingInProgressRef.current = true;

			saveElementaryHomeroomGrades({
				school_id: school_id,
				schedule_version_id: schedule_version_id,
				grade_groups: groupsToSave
			}).then(() => {
				lastSavedComparableRef.current = JSON.stringify(createComparableGroups(groupsToSave));
			}).catch((error) => {
				if(!saveErrorLoggedRef.current)
				{
					saveErrorLoggedRef.current = true;
					console.log('Error saving homeroom grades', error);
				}
			}).finally(() => {
				savingInProgressRef.current = false;
				if(pendingSaveRef.current)
				{
					pendingSaveRef.current = false;
					saveGroups(latestGradeGroupsRef.current);
				}
			});
		};

		saveGroups(gradeGroups);
	}, [gradeGroups, schoolInfo && schoolInfo.school_id, schoolInfo && schoolInfo.current_schedule_version_id, initialLoadComplete, initialHomeroomGroupsLoading]);

	return (
		<div className='mb-elem-container'>
		{(subscreenRestricted) ?
		(
			<div className='general-screen-message'>
				<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
				<h1>No Access</h1>
				<div>You do not have access to this screen</div>
			</div>
		):(subscreenLocked) ?
		(
			<div className='general-screen-message'>
				<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
				<h1>Temporarily Locked</h1>
				<div>This screen is locked until all other prior steps are completed</div>
			</div>
		):
		(
			<>
				<h1 className='progress-main-header'>Manage Homerooms</h1>
				<p className='progress-subheader elem-manage-homerooms-subheader'>Let us know what grades you need and which teachers should be assigned to each.</p>

				<div className='elem-manage-homerooms-grid'>
					{gradeGroups.map((gradeGroup) => {
						const teacherSearchOpen = !!gradeSearchOpen[gradeGroup.id];
						const teacherResults = gradeSearchResults[gradeGroup.id] || [];
						const gradeIsBeingEdited = editingGradeId === gradeGroup.id;
						return (
							<div className='elem-manage-homerooms-grade-card' key={gradeGroup.id}>
								<div className='elem-manage-homerooms-grade-header'>
									{gradeIsBeingEdited ?
										(
											<div className='elem-manage-homerooms-grade-editor'>
												<div className='elem-manage-homerooms-grade-dropdown'>
													<Dropdown data={gradeDropdownOptions} currentValue={editGradeValue} handleChange={setEditGradeValue}/>
												</div>
												{editGradeValue === CUSTOM_GRADE_VALUE &&
													<input className='elem-manage-homerooms-custom-grade-input' value={editCustomGradeName} onChange={(e) => setEditCustomGradeName(e.target.value)} placeholder='Custom grade name'/>
												}
												<div className='elem-manage-homerooms-grade-editor-actions'>
													<div className='btn blue-btn' onClick={saveEditedGrade}>Save</div>
													<div className='gray-link' onClick={() => setEditingGradeId(null)}>Cancel</div>
												</div>
											</div>
										):
										(
											<>
											<div className='elem-manage-homerooms-grade-heading'>
												<div className='elem-manage-homerooms-grade-kicker'>Grade Level</div>
												<div className='elem-manage-homerooms-grade-title'>{`${gradeGroup.label}`}</div>
												<div className='elem-manage-homerooms-grade-meta'>
													<div className='elem-manage-homerooms-grade-count-badge'>{gradeGroup.teacher_ids.length} {gradeGroup.teacher_ids.length === 1 ? 'teacher' : 'teachers'}</div>
												</div>
											</div>
											<div className='elem-manage-homerooms-grade-actions'>
												<FontAwesomeIcon className='gray-to-dark-blue-link cursor-pointer' icon={faPenToSquare} onClick={() => startEditGrade(gradeGroup.id)}/>
												<FontAwesomeIcon className='gray-to-red-link cursor-pointer' icon={faTrashCan} onClick={() => removeGrade(gradeGroup.id)}/>
											</div>
											</>
										)
									}
								</div>

								{!gradeIsBeingEdited &&
									<div className='elem-manage-homerooms-card-body'>
										{gradeGroup.teacher_ids.length === 0 &&
											<div className='specials-setup-display elem-manage-homerooms-display'>No teachers added yet...</div>
										}
										{gradeGroup.teacher_ids.map((teacherId) => {
											const teacher = teachers.find(tempTeacher => tempTeacher.teacher_id === teacherId);
											return (
												<div className='specials-setup-display elem-manage-homerooms-display' key={teacherId}>
													<div>{displayTeacherName(teacher)}</div>
													<div className='cursor-pointer' onClick={() => removeTeacherFromGrade(gradeGroup.id, teacherId)}><FontAwesomeIcon icon={faTimes}/></div>
												</div>
											);
										})}
									</div>
								}
								

								{teacherSearchOpen &&
									<div className='search-add-row-container elem-manage-homerooms-search-row'>
										<div className='search-add-search-container'>
											<div className='search-add-adding-input-container'>
												<input className='search-add-adding-input' onChange={(e) => fuzzySearchTeachers(gradeGroup.id, e.target.value)} placeholder='Search for teacher' autoFocus={true}/>
												<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
											</div>
											<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes} onClick={() => toggleTeacherSearch(gradeGroup.id)}/>
										</div>
										{teacherResults.length > 0 &&
											<div className='elem-suggestion-box-parent parent-div'>
												<div className='elem-suggestion-box'>
													{teacherResults.map((searchResult, index) => {
														const searchTeacherId = searchResult.teacher_id;
														const teacherSelected = gradeGroup.teacher_ids.includes(searchTeacherId);
														return (
															<div className='search-add-search-result' key={`${gradeGroup.id}-${searchTeacherId}-${index}`}>
																<div>{displayTeacherName(searchResult)}</div>
																<SearchAddButton selected={teacherSelected} onAdd={() => addTeacherToGrade(gradeGroup.id, searchTeacherId)} />
															</div>
														);
													})}
												</div>
											</div>
										}
									</div>
								}

								<div className='blue-link medium-text specials-setup-add-link' onClick={() => toggleTeacherSearch(gradeGroup.id)}>
									<FontAwesomeIcon icon={faCirclePlus}/>
									<span>Add Teacher</span>
								</div>
							</div>
						);
					})}

					{!addGradeOpen ?
						(
							<div className='elem-manage-homerooms-add-grade-card cursor-pointer' onClick={openAddGrade}>
								<FontAwesomeIcon icon={faCirclePlus}/>
								<div>Add A Grade</div>
							</div>
						):
						(
							<div className='elem-manage-homerooms-grade-card elem-manage-homerooms-new-grade-card'>
								<div className='specials-setup-label'>Add Grade Level</div>
								<div className='elem-manage-homerooms-grade-dropdown'>
									<Dropdown data={gradeDropdownOptions} currentValue={addGradeValue} handleChange={setAddGradeValue}/>
								</div>
								{addGradeValue === CUSTOM_GRADE_VALUE &&
									<input className='elem-manage-homerooms-custom-grade-input' value={addCustomGradeName} onChange={(e) => setAddCustomGradeName(e.target.value)} placeholder='Custom grade name'/>
								}
								<div className='elem-manage-homerooms-new-grade-actions'>
									<div className='btn blue-btn' onClick={saveNewGrade}>Add Grade</div>
									<div className='gray-link cursor-pointer' onClick={() => setAddGradeOpen(false)}>Cancel</div>
								</div>
							</div>
						)
					}
				</div>

				{(gradeGroups.length === 0 || !hasTeacherAssignments) &&
					<div className='elem-manage-homerooms-helper-text'>Add at least one grade and one teacher to continue.</div>
				}

				<div className='fixed-bottom-save-container'>
					<Button classes={['btn', 'btn-extra-large', 'green-btn', 'align-right', 'move-to-next-screen-btn']} selected={false} text='Go to Next Step' onClickFunction={handleMoveToNextScreen} />
				</div>
			</>
		)}
		</div>
	);
}
